import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FeedBanner,
  FeedDocument,
  InsuranceType,
  Newsletter,
  INSURANCE_TYPE_CONFIG,
} from '../utils/feedStore';
import { feedAPI } from '../services/api';

// ── Image compression (canvas-based, keeps images under ~200 KB) ──────────────
const compressImage = (file: File, maxW = 1400, quality = 0.78): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxW) { height = Math.round((height * maxW) / width); width = maxW; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });

interface Client { id: number; companyName: string; }

interface Props { clients: Client[]; }

// ── Helpers ───────────────────────────────────────────────────────────────────
const INSURANCE_TYPES: InsuranceType[] = ['cyber', 'professional-indemnity', 'public-liability', 'general'];
const DOC_CATEGORIES = ['policy', 'claim-form', 'guide', 'other'] as const;
const DOC_CAT_LABELS: Record<string, string> = {
  'policy': 'Policy Document', 'claim-form': 'Claim Form',
  'guide': 'Guide / Handbook', 'other': 'Other',
};

const DEFAULT_CATEGORIES = ['Cyber Security', 'Cost Management', 'Professional Advice', 'Policy Updates', 'Compliance', 'Industry News', 'General'];

function fmtDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtFileSize(bytes: number) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const subTabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13,
  fontWeight: active ? 700 : 500,
  borderBottom: active ? '3px solid #002060' : '3px solid transparent',
  background: 'transparent', color: active ? '#002060' : '#6b7280',
  transition: 'all 0.15s',
});

// Client targeting widget
const ClientTargetPicker: React.FC<{
  targetAll: boolean;
  targetClientIds: number[];
  clients: Client[];
  onChange: (targetAll: boolean, ids: number[]) => void;
}> = ({ targetAll, targetClientIds, clients, onChange }) => {
  const toggle = (id: number) => {
    const next = targetClientIds.includes(id)
      ? targetClientIds.filter(x => x !== id)
      : [...targetClientIds, id];
    onChange(false, next);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          style={{
            padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${targetAll ? '#001f3f' : '#e5e7eb'}`,
            background: targetAll ? '#001f3f' : 'white',
            color: targetAll ? 'white' : '#6b7280',
          }}
          onClick={() => onChange(true, [])}
        >
          All Clients
        </button>
        <button
          type="button"
          style={{
            padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${!targetAll ? '#001f3f' : '#e5e7eb'}`,
            background: !targetAll ? '#001f3f' : 'white',
            color: !targetAll ? 'white' : '#6b7280',
          }}
          onClick={() => onChange(false, targetClientIds)}
        >
          Specific Clients
        </button>
      </div>

      {!targetAll && (
        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 160,
          overflowY: 'auto', background: '#fafafa',
        }}>
          {clients.length === 0 ? (
            <p style={{ padding: '10px 14px', color: '#9ca3af', fontSize: 13, margin: 0 }}>No clients loaded.</p>
          ) : (
            clients.map(c => (
              <label key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                borderBottom: '1px solid #f3f4f6',
              }}>
                <input
                  type="checkbox"
                  checked={targetClientIds.includes(c.id)}
                  onChange={() => toggle(c.id)}
                  style={{ width: 14, height: 14 }}
                />
                {c.companyName}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const FeedManagement: React.FC<Props> = ({ clients }) => {
  const [subTab, setSubTab] = useState<'banners' | 'newsletters' | 'documents'>('banners');

  // ── Data state ─────────────────────────────────────────────────────────────
  const [banners, setBanners]         = useState<FeedBanner[]>([]);
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [documents, setDocuments]     = useState<FeedDocument[]>([]);
  const [isLoading, setIsLoading]     = useState(true);

  // ── Banner form ────────────────────────────────────────────────────────────
  const emptyBannerForm = {
    title: '', message: '', type: 'cyber' as InsuranceType,
    ctaLabel: 'Log Renewal Date', isActive: true, targetAll: true, targetClientIds: [] as number[],
  };
  const [bannerForm, setBannerForm] = useState({ ...emptyBannerForm });
  const [editingBannerId, setEditBannerId] = useState<string | null>(null);
  const [showBannerForm, setShowBannerForm] = useState(false);

  // ── Newsletter form ────────────────────────────────────────────────────────
  const emptyNlForm = {
    title: '', excerpt: '', body: '', category: 'Industry News',
    readTime: 3, isPublished: true, targetAll: true, targetClientIds: [] as number[],
    headerImage: '',
  };
  const [nlForm, setNlForm] = useState({ ...emptyNlForm });
  const [editingNlId, setEditNlId] = useState<string | null>(null);
  const [showNlForm, setShowNlForm] = useState(false);
  const [nlPreview, setNlPreview] = useState(false);

  // ── Document upload ────────────────────────────────────────────────────────
  const emptyDocForm = {
    name: '', description: '', category: 'policy' as FeedDocument['category'],
    targetAll: false, targetClientIds: [] as number[],
  };
  const [docForm, setDocForm] = useState({ ...emptyDocForm });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [showDocForm, setShowDocForm] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Feedback messages ──────────────────────────────────────────────────────
  const [feedMsg, setFeedMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const showMsg = (ok: boolean, text: string) => {
    setFeedMsg({ ok, text });
    setTimeout(() => setFeedMsg(null), 3500);
  };

  // ── Load ───────────────────────────────────────────────────────────────────
  const reload = useCallback(async () => {
    try {
      const [bRes, nRes, dRes] = await Promise.all([
        feedAPI.getBanners(),
        feedAPI.getNewsletters(),
        feedAPI.getDocuments(),
      ]);
      setBanners(Array.isArray(bRes.data) ? bRes.data : []);
      setNewsletters(Array.isArray(nRes.data) ? nRes.data : []);
      setDocuments(Array.isArray(dRes.data) ? dRes.data : []);
    } catch {
      // silently ignore — data stays as-is
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load data immediately — don't block on seedDefaults.
    // Seed runs in the background (no-op if content already exists).
    reload();
    feedAPI.seedDefaults().catch(() => {});
  }, [reload]);

  // ── Banner handlers ────────────────────────────────────────────────────────
  const openCreateBanner = () => {
    setBannerForm({ ...emptyBannerForm });
    setEditBannerId(null);
    setShowBannerForm(true);
  };

  const openEditBanner = (b: FeedBanner) => {
    setBannerForm({
      title: b.title, message: b.message, type: b.type,
      ctaLabel: b.ctaLabel, isActive: b.isActive,
      targetAll: b.targetAll, targetClientIds: [...b.targetClientIds],
    });
    setEditBannerId(b.id);
    setShowBannerForm(true);
  };

  const saveBanner = async () => {
    if (!bannerForm.title.trim() || !bannerForm.message.trim()) {
      showMsg(false, 'Title and message are required.'); return;
    }
    try {
      if (editingBannerId) {
        await feedAPI.updateBanner(editingBannerId, bannerForm);
        showMsg(true, 'Banner updated.');
      } else {
        await feedAPI.createBanner(bannerForm);
        showMsg(true, 'Banner created.');
      }
      setShowBannerForm(false);
      await reload();
    } catch {
      showMsg(false, 'Failed to save banner.');
    }
  };

  const deleteBanner = async (id: string) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await feedAPI.deleteBanner(id);
      await reload();
      showMsg(true, 'Banner deleted.');
    } catch {
      showMsg(false, 'Failed to delete banner.');
    }
  };

  const toggleBannerActive = async (b: FeedBanner) => {
    try {
      await feedAPI.updateBanner(b.id, { ...b, isActive: !b.isActive });
      await reload();
    } catch {
      showMsg(false, 'Failed to update banner.');
    }
  };

  // ── Newsletter handlers ────────────────────────────────────────────────────
  const openCreateNl = () => {
    setNlForm({ ...emptyNlForm });
    setEditNlId(null);
    setShowNlForm(true);
    setNlPreview(false);
  };

  const openEditNl = (n: Newsletter) => {
    setNlForm({
      title: n.title, excerpt: n.excerpt, body: n.body, category: n.category,
      readTime: n.readTime, isPublished: n.isPublished,
      targetAll: n.targetAll, targetClientIds: [...n.targetClientIds],
      headerImage: n.headerImage ?? '',
    });
    setEditNlId(n.id);
    setShowNlForm(true);
    setNlPreview(false);
  };

  const saveNl = async () => {
    if (!nlForm.title.trim() || !nlForm.excerpt.trim()) {
      showMsg(false, 'Title and excerpt are required.'); return;
    }
    try {
      if (editingNlId) {
        await feedAPI.updateNewsletter(editingNlId, nlForm);
        showMsg(true, 'Newsletter updated.');
      } else {
        await feedAPI.createNewsletter(nlForm);
        showMsg(true, 'Newsletter created.');
      }
      setShowNlForm(false);
      await reload();
    } catch {
      showMsg(false, 'Failed to save newsletter.');
    }
  };

  const deleteNl = async (id: string) => {
    if (!window.confirm('Delete this newsletter?')) return;
    try {
      await feedAPI.deleteNewsletter(id);
      await reload();
      showMsg(true, 'Newsletter deleted.');
    } catch {
      showMsg(false, 'Failed to delete newsletter.');
    }
  };

  const toggleNlPublished = async (n: Newsletter) => {
    try {
      await feedAPI.updateNewsletter(n.id, { ...n, isPublished: !n.isPublished });
      await reload();
    } catch {
      showMsg(false, 'Failed to update newsletter.');
    }
  };

  // ── Document handlers ──────────────────────────────────────────────────────
  const openDocForm = () => {
    setDocForm({ ...emptyDocForm });
    setDocFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowDocForm(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setDocFile(f);
    if (f && !docForm.name) {
      setDocForm(prev => ({ ...prev, name: f.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ') }));
    }
  };

  const uploadDoc = async () => {
    if (!docForm.name.trim()) { showMsg(false, 'Document name is required.'); return; }
    setUploadingDoc(true);
    try {
      if (docFile) {
        const reader = new FileReader();
        reader.onload = async e => {
          try {
            const full = (e.target?.result as string) ?? '';
            const base64 = full.includes(',') ? full.split(',')[1] : full;
            await feedAPI.createDocument({
              ...docForm,
              filename: docFile.name,
              fileData: base64,
              fileSize: docFile.size,
            });
            await reload();
            setShowDocForm(false);
            showMsg(true, 'Document uploaded successfully.');
          } catch {
            showMsg(false, 'Upload failed.');
          } finally {
            setUploadingDoc(false);
          }
        };
        reader.readAsDataURL(docFile);
      } else {
        // Metadata only (no file)
        await feedAPI.createDocument({ ...docForm, filename: '', fileData: '', fileSize: 0 });
        await reload();
        setShowDocForm(false);
        showMsg(true, 'Document entry created (no file attached).');
        setUploadingDoc(false);
      }
    } catch {
      showMsg(false, 'Upload failed.');
      setUploadingDoc(false);
    }
  };

  const deleteDoc = async (id: string) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await feedAPI.deleteDocument(id);
      await reload();
      showMsg(true, 'Document deleted.');
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Unknown error';
      showMsg(false, `Failed to delete document (${status ?? 'network error'}: ${msg})`);
      console.error('deleteDoc error:', err?.response ?? err);
    }
  };

  const downloadDoc = (doc: FeedDocument) => {
    if (!doc.fileData) { alert('No file attached to this document.'); return; }
    const byteArr = new Uint8Array(Array.from(atob(doc.fileData), c => c.charCodeAt(0)));
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = doc.filename || `${doc.name}.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Shared form styles ─────────────────────────────────────────────────────
  const formPanel: React.CSSProperties = {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: 24, marginBottom: 20,
  };
  const formLabel: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 700, color: '#374151',
    marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.8,
  };
  const formInput: React.CSSProperties = {
    width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  };
  const formRow: React.CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 };
  const formGroup = (flex = '1 1 200px'): React.CSSProperties => ({ display: 'flex', flexDirection: 'column', gap: 0, flex });

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 4px', color: '#001f3f' }}>My Feed Management</h2>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
          Manage the personalised content that appears on the My Feed page for your clients.
          Banners, newsletters and documents can all be targeted to specific clients.
        </p>
      </div>

      {/* Feedback toast */}
      {feedMsg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: feedMsg.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${feedMsg.ok ? '#86efac' : '#fca5a5'}`,
          color: feedMsg.ok ? '#15803d' : '#b91c1c',
        }}>
          {feedMsg.ok ? '✓' : '✗'} {feedMsg.text}
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        {(['banners', 'newsletters', 'documents'] as const).map((tab) => {
          const count = tab === 'banners' ? banners.length : tab === 'newsletters' ? newsletters.length : documents.length;
          return (
            <button key={tab} style={subTabStyle(subTab === tab)} onClick={() => setSubTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}{' '}
              <span style={{ marginLeft: 4, background: isLoading ? '#f3f4f6' : '#e5e7eb', borderRadius: 99, padding: '1px 6px', fontSize: 11, color: isLoading ? '#9ca3af' : 'inherit' }}>
                {isLoading ? '…' : count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Loading skeleton while initial data fetches */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 14 }}>
          <div style={{ marginBottom: 8, fontSize: 22 }}>&#9203;</div>
          Loading feed content…
        </div>
      )}

      {/* ── BANNERS ────────────────────────────────────────────────────────── */}
      {subTab === 'banners' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: '0 0 2px', color: '#001f3f' }}>Feed Banners</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Clickable banners shown at the top of My Feed. Users can set renewal dates directly from the banner.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openCreateBanner} style={{ fontSize: 13 }}>
              + New Banner
            </button>
          </div>

          {/* Create / Edit form */}
          {showBannerForm && (
            <div style={formPanel}>
              <h4 style={{ margin: '0 0 16px', color: '#001f3f' }}>
                {editingBannerId ? 'Edit Banner' : 'Create New Banner'}
              </h4>
              <div style={formRow}>
                <div style={formGroup('2 1 300px')}>
                  <label style={formLabel}>Title *</label>
                  <input style={formInput} value={bannerForm.title}
                    onChange={e => setBannerForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Cyber Insurance Renewal Reminder" />
                </div>
                <div style={formGroup('1 1 160px')}>
                  <label style={formLabel}>Insurance Type *</label>
                  <select style={formInput} value={bannerForm.type}
                    onChange={e => setBannerForm(p => ({ ...p, type: e.target.value as InsuranceType }))}>
                    {INSURANCE_TYPES.map(t => (
                      <option key={t} value={t}>{INSURANCE_TYPE_CONFIG[t].label}</option>
                    ))}
                  </select>
                </div>
                <div style={formGroup('1 1 160px')}>
                  <label style={formLabel}>CTA Button Label</label>
                  <input style={formInput} value={bannerForm.ctaLabel}
                    onChange={e => setBannerForm(p => ({ ...p, ctaLabel: e.target.value }))}
                    placeholder="Log Renewal Date" />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={formLabel}>Message Body *</label>
                <textarea
                  style={{ ...formInput, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
                  value={bannerForm.message}
                  onChange={e => setBannerForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Brief description shown on the banner…"
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={formLabel}>Target Clients</label>
                <ClientTargetPicker
                  targetAll={bannerForm.targetAll}
                  targetClientIds={bannerForm.targetClientIds}
                  clients={clients}
                  onChange={(all, ids) => setBannerForm(p => ({ ...p, targetAll: all, targetClientIds: ids }))}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={bannerForm.isActive} style={{ width: 15, height: 15 }}
                    onChange={e => setBannerForm(p => ({ ...p, isActive: e.target.checked }))} />
                  <span style={{ fontWeight: 600 }}>Active (show to clients)</span>
                </label>
              </div>

              {/* Preview strip */}
              <div style={{
                borderRadius: 10, padding: '14px 20px', marginBottom: 16,
                background: INSURANCE_TYPE_CONFIG[bannerForm.type].bgGradient,
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <span style={{ fontSize: 28 }}>{INSURANCE_TYPE_CONFIG[bannerForm.type].icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
                    {INSURANCE_TYPE_CONFIG[bannerForm.type].label}
                  </div>
                  <div style={{ fontWeight: 700, color: 'white', fontSize: 14, marginBottom: 2 }}>
                    {bannerForm.title || 'Banner title…'}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bannerForm.message || 'Message text…'}
                  </div>
                </div>
                <span style={{ background: 'white', color: INSURANCE_TYPE_CONFIG[bannerForm.type].color, padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700 }}>
                  {bannerForm.ctaLabel || 'CTA →'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={saveBanner} style={{ fontSize: 13 }}>
                  {editingBannerId ? 'Save Changes' : 'Create Banner'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowBannerForm(false)} style={{ fontSize: 13 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Banners table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: 24 }}>
                    No banners yet. Create one above.
                  </td></tr>
                )}
                {banners.map(b => {
                  const cfg = INSURANCE_TYPE_CONFIG[b.type];
                  return (
                    <tr key={b.id} style={{ fontSize: 13 }}>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                          background: `${cfg.color}18`, color: cfg.color,
                        }}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: '#111827' }}>{b.title}</td>
                      <td style={{ color: '#6b7280', fontSize: 12 }}>
                        {b.targetAll ? 'All clients' : `${b.targetClientIds.length} client${b.targetClientIds.length !== 1 ? 's' : ''}`}
                      </td>
                      <td>
                        <span
                          onClick={() => toggleBannerActive(b)}
                          style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: 99,
                            fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            background: b.isActive ? '#dcfce7' : '#f3f4f6',
                            color: b.isActive ? '#166534' : '#6b7280',
                          }}
                          title="Click to toggle"
                        >
                          {b.isActive ? '● Active' : '○ Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#9ca3af' }}>{fmtDate(b.createdAt)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => openEditBanner(b)}>Edit</button>
                          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px', color: '#dc2626' }}
                            onClick={() => deleteBanner(b.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── NEWSLETTERS ──────────────────────────────────────────────────────── */}
      {subTab === 'newsletters' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: '0 0 2px', color: '#001f3f' }}>Newsletters</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Blog-style posts shown in the Yourshield Updates section. Full HTML body supported.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openCreateNl} style={{ fontSize: 13 }}>
              + New Newsletter
            </button>
          </div>

          {/* Create / Edit form */}
          {showNlForm && (
            <div style={formPanel}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0, color: '#001f3f' }}>
                  {editingNlId ? 'Edit Newsletter' : 'New Newsletter'}
                </h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={{ ...subTabStyle(!nlPreview), padding: '4px 12px', fontSize: 12 }}
                    onClick={() => setNlPreview(false)}>Edit</button>
                  <button
                    style={{ ...subTabStyle(nlPreview), padding: '4px 12px', fontSize: 12 }}
                    onClick={() => setNlPreview(true)}>Preview</button>
                </div>
              </div>

              {!nlPreview ? (
                <>
                  {/* ── Header image upload ── */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={formLabel}>Header Image <span style={{ color: '#9ca3af', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional — replaces the colour gradient)</span></label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ fontSize: 13 }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const compressed = await compressImage(f);
                          setNlForm(p => ({ ...p, headerImage: compressed }));
                        }}
                      />
                      {nlForm.headerImage && (
                        <button
                          type="button"
                          onClick={() => setNlForm(p => ({ ...p, headerImage: '' }))}
                          style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                        >
                          ✕ Remove image
                        </button>
                      )}
                    </div>
                    {nlForm.headerImage && (
                      <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', height: 120, position: 'relative', maxWidth: 420 }}>
                        <img src={nlForm.headerImage} alt="Header preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'flex-end', padding: '10px 14px' }}>
                          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600 }}>Preview with overlay</span>
                        </div>
                      </div>
                    )}
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '5px 0 0' }}>Images are auto-compressed. Landscape photos (16:9) work best.</p>
                  </div>

                  <div style={formRow}>
                    <div style={formGroup('3 1 300px')}>
                      <label style={formLabel}>Title *</label>
                      <input style={formInput} value={nlForm.title}
                        onChange={e => setNlForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. 5 Ways to Reduce Your Fleet Insurance Premium" />
                    </div>
                    <div style={formGroup('1 1 150px')}>
                      <label style={formLabel}>Category</label>
                      <input
                        style={formInput}
                        list="feed-categories"
                        value={nlForm.category}
                        onChange={e => setNlForm(p => ({ ...p, category: e.target.value }))}
                        placeholder="Category…"
                      />
                      <datalist id="feed-categories">
                        {DEFAULT_CATEGORIES.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                    <div style={formGroup('1 1 100px')}>
                      <label style={formLabel}>Read Time (min)</label>
                      <input type="number" min={1} max={60} style={formInput} value={nlForm.readTime}
                        onChange={e => setNlForm(p => ({ ...p, readTime: Number(e.target.value) }))} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={formLabel}>Excerpt * <span style={{ color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>(shown on the card)</span></label>
                    <textarea
                      style={{ ...formInput, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
                      value={nlForm.excerpt}
                      onChange={e => setNlForm(p => ({ ...p, excerpt: e.target.value }))}
                      placeholder="Brief summary shown on the card before the user opens it…"
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={formLabel}>
                      Body (HTML)
                      <span style={{ color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}> — use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt; etc.</span>
                    </label>
                    <textarea
                      style={{ ...formInput, minHeight: 200, resize: 'vertical', fontFamily: 'Consolas, monospace', fontSize: 12 }}
                      value={nlForm.body}
                      onChange={e => setNlForm(p => ({ ...p, body: e.target.value }))}
                      spellCheck={false}
                      placeholder="<h2>Heading</h2><p>Content…</p>"
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={formLabel}>Target Clients</label>
                    <ClientTargetPicker
                      targetAll={nlForm.targetAll}
                      targetClientIds={nlForm.targetClientIds}
                      clients={clients}
                      onChange={(all, ids) => setNlForm(p => ({ ...p, targetAll: all, targetClientIds: ids }))}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', userSelect: 'none', marginBottom: 16 }}>
                    <input type="checkbox" checked={nlForm.isPublished} style={{ width: 15, height: 15 }}
                      onChange={e => setNlForm(p => ({ ...p, isPublished: e.target.checked }))} />
                    <span style={{ fontWeight: 600 }}>Published (visible to clients immediately)</span>
                  </label>
                </>
              ) : (
                /* Preview pane */
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                  <div style={{ background: 'linear-gradient(135deg, #001f3f, #003366)', padding: '22px 24px', minHeight: 100 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', marginBottom: 8 }}>
                      {nlForm.category} · {nlForm.readTime} min read
                    </div>
                    <div style={{ fontSize: 19, fontWeight: 700, color: 'white' }}>{nlForm.title || 'Untitled'}</div>
                  </div>
                  <div style={{ padding: 24, fontSize: 14, lineHeight: 1.75, color: '#374151' }}>
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: nlForm.body || '<p style="color:#9ca3af">Body content will appear here…</p>' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={saveNl} style={{ fontSize: 13 }}>
                  {editingNlId ? 'Save Changes' : 'Publish Newsletter'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowNlForm(false)} style={{ fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Newsletters table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newsletters.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: 24 }}>
                    No newsletters yet.
                  </td></tr>
                )}
                {newsletters.map(n => (
                  <tr key={n.id} style={{ fontSize: 13 }}>
                    <td style={{ fontWeight: 600, color: '#111827', maxWidth: 300 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{n.readTime} min read</div>
                    </td>
                    <td>
                      <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                        {n.category}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {n.targetAll ? 'All clients' : `${n.targetClientIds.length} client${n.targetClientIds.length !== 1 ? 's' : ''}`}
                    </td>
                    <td>
                      <span
                        onClick={() => toggleNlPublished(n)}
                        style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 99,
                          fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          background: n.isPublished ? '#dcfce7' : '#fef3c7',
                          color: n.isPublished ? '#166534' : '#92400e',
                        }}
                        title="Click to toggle"
                      >
                        {n.isPublished ? '● Published' : '○ Draft'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#9ca3af' }}>{fmtDate(n.publishedAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}
                          onClick={() => openEditNl(n)}>Edit</button>
                        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px', color: '#dc2626' }}
                          onClick={() => deleteNl(n.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DOCUMENTS ────────────────────────────────────────────────────────── */}
      {subTab === 'documents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: '0 0 2px', color: '#001f3f' }}>Document Store</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Upload PDFs (policy documents, claim forms, guides). Clients can download from their My Feed page.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openDocForm} style={{ fontSize: 13 }}>
              + Upload Document
            </button>
          </div>

          {/* Upload form */}
          {showDocForm && (
            <div style={formPanel}>
              <h4 style={{ margin: '0 0 16px', color: '#001f3f' }}>Upload New Document</h4>

              <div style={{ marginBottom: 14 }}>
                <label style={formLabel}>PDF File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'block', fontSize: 13, padding: '6px 0' }}
                />
                {docFile && (
                  <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>
                    ✓ {docFile.name} ({fmtFileSize(docFile.size)})
                  </div>
                )}
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>
                  Leave empty to create a placeholder entry (clients will see "Pending upload").
                </p>
              </div>

              <div style={formRow}>
                <div style={formGroup('2 1 260px')}>
                  <label style={formLabel}>Document Name *</label>
                  <input style={formInput} value={docForm.name}
                    onChange={e => setDocForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Fleet Insurance Policy Schedule" />
                </div>
                <div style={formGroup('1 1 150px')}>
                  <label style={formLabel}>Category *</label>
                  <select style={formInput} value={docForm.category}
                    onChange={e => setDocForm(p => ({ ...p, category: e.target.value as FeedDocument['category'] }))}>
                    {DOC_CATEGORIES.map(c => <option key={c} value={c}>{DOC_CAT_LABELS[c]}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={formLabel}>Description</label>
                <textarea
                  style={{ ...formInput, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
                  value={docForm.description}
                  onChange={e => setDocForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of the document's purpose…"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={formLabel}>Target Clients</label>
                <ClientTargetPicker
                  targetAll={docForm.targetAll}
                  targetClientIds={docForm.targetClientIds}
                  clients={clients}
                  onChange={(all, ids) => setDocForm(p => ({ ...p, targetAll: all, targetClientIds: ids }))}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={uploadDoc} disabled={uploadingDoc} style={{ fontSize: 13 }}>
                  {uploadingDoc ? 'Uploading…' : 'Save Document'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowDocForm(false)} style={{ fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Documents table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Target</th>
                  <th>File</th>
                  <th>Added</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: 24 }}>
                    No documents yet.
                  </td></tr>
                )}
                {documents.map(d => (
                  <tr key={d.id} style={{ fontSize: 13 }}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{d.name}</div>
                      {d.description && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                        {DOC_CAT_LABELS[d.category]}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {d.targetAll ? 'All clients' : `${d.targetClientIds.length} client${d.targetClientIds.length !== 1 ? 's' : ''}`}
                    </td>
                    <td>
                      {d.fileData ? (
                        <span style={{ color: '#059669', fontSize: 12, fontWeight: 600 }}>
                          ✓ {fmtFileSize(d.fileSize)}
                        </span>
                      ) : (
                        <span style={{ color: '#d97706', fontSize: 12, fontWeight: 600 }}>⏳ Pending</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#9ca3af' }}>{fmtDate(d.uploadedAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {d.fileData && (
                          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => downloadDoc(d)}>Download</button>
                        )}
                        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px', color: '#dc2626' }}
                          onClick={() => deleteDoc(d.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedManagement;
